import React from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export type CountingRuleNoteTone = 'rule' | 'note';

export interface ICountingRuleNoteProps {
  /** `BreakdownFixture.countingRule` — the denominator disclosure, always payload, never authored here. */
  text?: string;
  tone?: CountingRuleNoteTone;
  className?: string;
}

// `589:33328` reads this box's own hue as `#F3B95F`, a near-neighbour of `warning` `#F5A623` —
// collapsed onto the existing token rather than added as a second amber (plan.md AF-3). The channels
// come from `--is-warning-rgb` rather than a literal, because `rgba()` cannot take a hex var and the
// literal would have kept painting the dark amber under the flag; the dark reading of that token is
// `warning`'s own #F5A623 verbatim.
const WARNING_RGB = tokens.color['warning-rgb'];

/* `589:33328`'s counting-rule box: 610x70.28, r10, fill 10% / border 35% of the warning hue. Only
   the `rule` tone gets the callout treatment; `note` (IndustrySection's own bare captions) stays
   the plain text it already is. PLA-8815. */
const Note = styled.div<{ $tone: CountingRuleNoteTone }>`
  > * + * {
    margin-top: ${tokens.space['4']};
  }

  ${({ $tone }) =>
    $tone === 'rule'
      ? `
    padding: 13px 14px;
    border: 1px solid rgba(${WARNING_RGB}, 0.35);
    border-radius: 10px;
    background: rgba(${WARNING_RGB}, 0.1);
  `
      : ''}
`;

const Heading = styled.h4`
  margin: 0;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
  text-transform: uppercase;
`;

const Text = styled.p<{ $tone: CountingRuleNoteTone }>`
  margin: 0;
  color: ${({ $tone }) => ($tone === 'rule' ? tokens.color.warning : tokens.color['text-primary'])};
  font-size: ${({ $tone }) =>
    $tone === 'rule' ? tokens.type['body-size'] : tokens.type['label-size']};
  font-weight: ${({ $tone }) =>
    $tone === 'rule' ? tokens.type['card-title-weight'] : tokens.type['body-weight']};
  line-height: ${({ $tone }) =>
    $tone === 'rule' ? tokens.type['body-line'] : tokens.type['label-line']};
`;

const CountingRuleNote: React.FC<ICountingRuleNoteProps> = ({ text, tone = 'rule', className }) => {
  const { t } = useMultiLanguageTranslation();
  const disclosure = typeof text === 'string' ? text.trim() : '';

  if (disclosure === '') {
    return null;
  }

  return (
    <Note $tone={tone} className={className} data-tone={tone} data-testid="counting-rule-note">
      {tone === 'rule' ? (
        <Heading data-testid="counting-rule-note-heading">
          {t(`${TCustomerInsights}.Breakdowns.CountingRuleHeading`)}
        </Heading>
      ) : null}
      <Text $tone={tone} data-testid="counting-rule-note-text">
        {disclosure}
      </Text>
    </Note>
  );
};

export default CountingRuleNote;
